import React, { useEffect, useRef } from 'react';
import { Box, Button, MenuItem, Select, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { baseColors } from '../../../Themes/colors';
var getRowOptions = function () { return [50, 100, 200, 250]; };
var BacklogPagination = function (_a) {
    var currentPage = _a.currentPage, perPageCount = _a.perPageCount, totalCount = _a.totalCount, onPageChange = _a.onPageChange, onPerPageChange = _a.onPerPageChange, _b = _a.className, className = _b === void 0 ? '' : _b, _c = _a.defaultPerPageCount, defaultPerPageCount = _c === void 0 ? 50 : _c, _d = _a.resetOnFirstLoad, resetOnFirstLoad = _d === void 0 ? true : _d;
    var totalRecords = totalCount > 0 ? totalCount : 0;
    var shouldShowPagination = totalRecords > 5;
    var initializedRef = useRef(false);
    useEffect(function () {
        if (resetOnFirstLoad && !initializedRef.current && totalRecords > 0) {
            initializedRef.current = true;
            onPerPageChange(defaultPerPageCount);
            onPageChange(1);
        }
    }, [totalRecords]);
    if (!shouldShowPagination) {
        return null;
    }
    var rowOptions = getRowOptions();
    var totalPages = Math.max(1, Math.ceil(totalRecords / perPageCount));
    var canGoPrev = currentPage > 1;
    var canGoNext = currentPage < totalPages;
    var getVisiblePages = function () {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, function (_, idx) { return idx + 1; });
        }
        if (currentPage <= 3 || currentPage >= totalPages - 2) {
            return [1, 2, 3, 'ellipsis', totalPages - 2, totalPages - 1, totalPages];
        }
        return [1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages];
    };
    var visiblePages = getVisiblePages();
    var handleRowsChange = function (value) {
        onPerPageChange(value);
        onPageChange(1);
    };
    return (React.createElement(Stack, { direction: "row", justifyContent: "space-between", alignItems: "center", width: "100%", py: 0.5, className: "backlog-pagination-root ".concat(className).trim() },
        React.createElement(Stack, { direction: "row", alignItems: "center", spacing: 1 },
            React.createElement(Typography, { variant: "body2", sx: { fontSize: 14, fontWeight: 500, color: baseColors.greyPreset[10], whiteSpace: 'nowrap' } }, "Rows per page:"),
            React.createElement(Select, { value: perPageCount, onChange: function (e) { return handleRowsChange(Number(e.target.value)); }, size: "small", sx: {
                    fontSize: 14,
                    fontWeight: 600,
                    height: 34,
                    minWidth: 70,
                    color: baseColors.greyPreset[10],
                    backgroundColor: baseColors.fullWhite,
                    '.MuiOutlinedInput-notchedOutline': {
                        borderColor: baseColors.greyPreset[40],
                        borderRadius: '8px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: baseColors.greyPreset[30],
                    },
                    '.MuiSelect-select': { py: 0, px: 1.5 },
                } }, rowOptions.map(function (opt) { return (React.createElement(MenuItem, { key: opt, value: opt, sx: { fontSize: 14 } }, opt)); }))),
        React.createElement(Stack, { direction: "row", alignItems: "center", className: "backlog-pagination-control", sx: {
                border: "1px solid ".concat(baseColors.greyPreset[40]),
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: baseColors.fullWhite,
            } },
            React.createElement(Button, { onClick: function () { return canGoPrev && onPageChange(currentPage - 1); }, disabled: !canGoPrev, startIcon: React.createElement(ArrowBackIcon, { sx: { fontSize: 14 } }), sx: {
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5,
                    minHeight: 34,
                    borderRadius: 0,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 14,
                    color: baseColors.greyPreset[10],
                    borderRight: "1px solid ".concat(baseColors.greyPreset[40]),
                    '&.Mui-disabled': {
                        color: baseColors.greyPreset[30],
                    },
                } }, "Previous"),
            visiblePages.map(function (page, idx) {
                if (page === 'ellipsis') {
                    return (React.createElement(Box, { key: "ellipsis-".concat(idx), sx: {
                            minWidth: 34,
                            height: 34,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRight: "1px solid ".concat(baseColors.greyPreset[40]),
                            color: baseColors.greyPreset[20],
                            fontSize: 14,
                        } }, "..."));
                }
                var isActive = currentPage === page;
                return (React.createElement(Box, { key: page, onClick: function () { return onPageChange(page); }, sx: {
                        minWidth: 34,
                        height: 34,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderRight: "1px solid ".concat(baseColors.greyPreset[40]),
                        backgroundColor: isActive ? baseColors.greyPreset[50] : baseColors.fullWhite,
                        color: baseColors.greyPreset[10],
                    } },
                    React.createElement(Typography, { variant: "body2", fontWeight: isActive ? 700 : 500 }, page)));
            }),
            React.createElement(Button, { onClick: function () { return canGoNext && onPageChange(currentPage + 1); }, disabled: !canGoNext, endIcon: React.createElement(ArrowForwardIcon, { sx: { fontSize: 14 } }), sx: {
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5,
                    minHeight: 34,
                    borderRadius: 0,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 14,
                    color: baseColors.greyPreset[10],
                    '&.Mui-disabled': {
                        color: baseColors.greyPreset[30],
                    },
                } }, "Next"))));
};
export default React.memo(BacklogPagination);
